import React, { useEffect, useRef, useState } from "react";
import S from "./CardLinks.styled";
import { Flex, MainInner, MainSection, motionAnimation } from "@/styles/global";
import { Body, H3, SmallHeading2 } from "@/styles/typography";
import { ColorOptions, IWPImage, IWPLink, IWPTitleParts } from "@/src/types";
import { BackgroundImage, Icon } from "@/styles/components";
import TitleParts from "@/components/global-components/TitleParts";
import { gatsbyImageHelper, isLight } from "@/src/helpers";
import { motion } from "framer-motion";

export interface CardLinksProps {
  blockId: string;
  backgroundColor: ColorOptions;
  title: string;
  titleParts: IWPTitleParts;
  cards: {
    link: IWPLink;
    image: IWPImage;
    title: string;
    subTitle: string;
  }[];
}

const CardLinks: React.FC<CardLinksProps> = ({
  blockId = "",
  backgroundColor,
  title,
  titleParts,
  cards,
}) => {
  backgroundColor = backgroundColor || "white";
  const [scrolls, setScrolls] = useState(false);
  const contentWrapper = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const checkScrollability = () => {
      if (contentWrapper.current) {
        setScrolls(
          contentWrapper.current.scrollWidth >
            contentWrapper.current.clientWidth
        );
      }
    };

    checkScrollability();
    window.addEventListener("resize", checkScrollability);

    return () => {
      window.removeEventListener("resize", checkScrollability);
    };
  }, [cards]);

  useEffect(() => {
    if (scrolls && window.innerWidth >= 1024 && contentWrapper.current) {
      // Automatically scroll one card to the right on load when scrolls === true
      contentWrapper.current.scrollLeft += 250;
    }
  }, [scrolls]);

  const handlePress = (element: HTMLDivElement, step: number) => {
    let scrollAmount = 0;
    const slideTimer = setInterval(() => {
      // Scroll by the step value (either left or right)
      element.scrollLeft += step;
      scrollAmount += Math.abs(step);

      // Check if we've scrolled past the threshold
      if (scrollAmount >= 100) {
        clearInterval(slideTimer);
      }

      // Check if the scroll position is at the end
      if (element.scrollLeft + element.clientWidth >= element.scrollWidth) {
        // If we're at the end, scroll back to the start
        element.scrollLeft = 0;
        clearInterval(slideTimer);
      }
    }, 25);
  };

  const NavigationButton = ({ left }: { left?: boolean }) => (
    <div
      onClick={() =>
        handlePress(contentWrapper.current as HTMLDivElement, left ? -332 : 332)
      }
    >
      <Icon src={left ? "ArrowLeft" : "ArrowRight"} width={24} />
    </div>
  );

  const renderCards = cards?.map(({ link, image, title, subTitle }, i) => {
    const imageData = gatsbyImageHelper(image);

    const CardContent = () => (
      <>
        {imageData && <S.Image image={imageData} alt={image.altText} />}
        <S.TextWrapper>
          <Flex $column>
            {title && <H3 color="white">{title}</H3>}
            {subTitle && (
              <Body color="white" size={16}>
                {subTitle}
              </Body>
            )}
          </Flex>
          <S.Button>
            <SmallHeading2 color={"white"}>{link?.title}</SmallHeading2>
            <Icon src="ArrowRightOutline" width={14} color="red" />
          </S.Button>
        </S.TextWrapper>
      </>
    );
    return (
      <S.CardWrapper key={i}>
        {link?.target === "_blank" ? (
          <S.CardA href={link?.url} target="_blank" key={i} id={i.toString()}>
            <CardContent />
          </S.CardA>
        ) : (
          <S.Card
            to={link?.url}
            target={link?.target}
            key={i}
            id={i.toString()}
          >
            <CardContent />
          </S.Card>
        )}
      </S.CardWrapper>
    );
  });

  return (
    <S.Section id={blockId} as={motion.section} {...motionAnimation}>
      <S.Wrapper $backgroundColor={backgroundColor}>
        <MainInner>
          <Flex $justify="between" $width={"100%"} $margin="0 0 32px 0">
            <H3
              size={20}
              color={
                backgroundColor === "yellow"
                  ? "brown"
                  : isLight(backgroundColor)
                    ? "black"
                    : "creme"
              }
            >
              {title?.toLocaleUpperCase()}
            </H3>
            {titleParts && (
              <H3 size={20}>
                <TitleParts
                  titleParts={titleParts}
                  backgroundColor={backgroundColor}
                />
              </H3>
            )}
            {scrolls && (
              <Flex $gap={24}>
                <NavigationButton left />
                <NavigationButton />
              </Flex>
            )}
          </Flex>
        </MainInner>
        <S.ScrollContainer ref={contentWrapper}>
          <S.Container>{renderCards}</S.Container>
        </S.ScrollContainer>
      </S.Wrapper>
    </S.Section>
  );
};

export default CardLinks;
